import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Send, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/lib/api/client";
import { createRequest, type RequestDetailOut } from "@/lib/api/requests";
import { cn } from "@/lib/cn";

import { MAX_FILES_PER_JOB, formatBytes } from "./constants";
import FileItem from "./FileItem";
import UploadDropzone from "./UploadDropzone";
import { useUploadFiles } from "./useUploadFiles";

const isZip = (f: File) => f.name.toLowerCase().endsWith(".zip");

export default function UploadPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [batchName, setBatchName] = useState("");
  const [autoReview, setAutoReview] = useState(false);
  const [progressPct, setProgressPct] = useState(0);
  const [mixError, setMixError] = useState<string | null>(null);

  const { entries, validFiles, hasInvalid, addFiles, removeFile, reset } = useUploadFiles();

  const zips = validFiles.filter(isZip);
  const pdfs = validFiles.filter((f) => !isZip(f));

  const mutation = useMutation<RequestDetailOut, ApiError | Error>({
    mutationFn: () => {
      const onUploadProgress = (event: { loaded: number; total?: number }) => {
        if (event.total) {
          setProgressPct(Math.round((event.loaded * 100) / event.total));
        }
      };
      const options = { title: batchName, autoReview, locale: i18n.language, onUploadProgress };
      if (zips.length === 1) {
        return createRequest({ zip: zips[0], ...options });
      }
      return createRequest({ files: pdfs, ...options });
    },
    onSuccess: (req) => {
      reset();
      setProgressPct(0);
      // 업로드 직후엔 처리 중이므로, 의뢰 상세에서 자동 폴링이 진행 상황을 보여준다.
      navigate(`/requests/${req.id}`);
    },
    onError: () => {
      setProgressPct(0);
    },
  });

  const totalBytes = validFiles.reduce((acc, f) => acc + f.size, 0);

  const submit = () => {
    setMixError(null);
    if (zips.length > 1) {
      setMixError(t("upload.errors.multipleZip"));
      return;
    }
    if (zips.length === 1 && pdfs.length > 0) {
      setMixError(t("upload.errors.zipAndPdf"));
      return;
    }
    mutation.mutate();
  };

  const submittable = validFiles.length > 0 && !mutation.isPending;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("upload.title")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("upload.description")}</p>
      </header>

      <UploadDropzone onAdd={addFiles} disabled={mutation.isPending} />

      {/* 파일 리스트 */}
      {entries.length > 0 && (
        <section
          aria-label={t("upload.list.aria")}
          className="rounded-lg border border-slate-200 bg-white p-4"
        >
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-medium text-slate-700">
              {t("upload.list.selected")}{" "}
              <span className="text-slate-400">
                {entries.length} / {MAX_FILES_PER_JOB}
              </span>
            </p>
            <p className="text-xs text-slate-400">
              {t("upload.list.totalSummary", {
                total: formatBytes(totalBytes),
                count: validFiles.length,
              })}
            </p>
          </div>

          <ul className="space-y-1.5">
            {entries.map((entry) => (
              <FileItem
                key={entry.key}
                entry={entry}
                onRemove={removeFile}
                disabled={mutation.isPending}
              />
            ))}
          </ul>

          {hasInvalid && (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>{t("upload.list.invalidNotice")}</span>
            </div>
          )}
        </section>
      )}

      {/* 배치명 + 제출 */}
      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <label htmlFor="batch-name" className="block text-sm font-medium text-slate-700">
          {t("upload.batchName.label")}{" "}
          <span className="text-xs text-slate-400">{t("upload.batchName.optional")}</span>
        </label>
        <input
          id="batch-name"
          type="text"
          value={batchName}
          onChange={(e) => setBatchName(e.target.value)}
          disabled={mutation.isPending}
          maxLength={100}
          placeholder={t("upload.batchName.placeholder")}
          className="mt-1.5 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-slate-50"
        />

        {/* M26 — 자동 검수 */}
        <label className="mt-4 flex cursor-pointer items-start gap-2.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
          <input
            type="checkbox"
            checked={autoReview}
            onChange={(e) => setAutoReview(e.target.checked)}
            disabled={mutation.isPending}
            className="mt-0.5 h-4 w-4 cursor-pointer accent-blue-600"
          />
          <span>
            <span className="block text-sm font-medium text-slate-700">
              {t("upload.autoReview.label")}
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">
              {t("upload.autoReview.hint")}
            </span>
          </span>
        </label>

        {/* 진행률 바 */}
        {mutation.isPending && (
          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
              <span>{t("upload.progress.uploading")}</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full bg-blue-500 transition-[width] duration-200 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {(mutation.isError || mixError) && (
          <div
            role="alert"
            className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              {mixError ??
                (mutation.error instanceof ApiError
                  ? mutation.error.detail
                  : mutation.error?.message)}
            </span>
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={mutation.isPending || entries.length === 0}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("common.actions.removeAll")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!submittable}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow",
              "transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-60",
            )}
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {t("upload.actions.submit", { count: validFiles.length })}
          </button>
        </div>
      </section>
    </div>
  );
}
